/**
 * Farmer module route definitions.
 *
 * Every entry maps exactly to the route paths that existed before the
 * refactor so that NO URL changes.
 *
 * `role` is kept for backward compatibility; `permission` is the primary
 * access check used by ProtectedRoute.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';

import FarmerDashboard from './pages/FarmerDashboard';
import FarmManagement from './pages/FarmManagement';
import PondsOverview from './pages/PondsOverview';
import SinglePondDashboard from './pages/SinglePondDashboard';
import StockingDetails from './pages/StockingDetails';
import PolicyManagement from './pages/PolicyManagement';
import PolicyDetailView from './pages/PolicyDetailView';
import ClaimsManagement from './pages/ClaimsManagement';
import DocumentsManagement from './pages/DocumentsManagement';
import RequestManagement from './pages/RequestManagement';
import ClaimCalculator from './pages/ClaimCalculator';
import PerilsInformation from './pages/PerilsInformation';
import PolicyTerms from './pages/PolicyTerms';
import PremiumCalculator from './pages/PremiumCalculator';
import HelpCenter from './pages/HelpCenter';
import KYCSubmission from './pages/KYCSubmission';
import AlertsPage from './pages/AlertsPage';

// ── New merged pages ─────────────────────────────────────────
import CreditEscrowPage from './pages/CreditEscrowPage';
import HarvestSettlementPage from './pages/HarvestSettlementPage';
import InsuranceClaimsPage from './pages/InsuranceClaimsPage';

const farmerRoutes = [
  // ═══════════════════════════════════════════════════════════
  // COMMAND CENTER
  // ═══════════════════════════════════════════════════════════
  { path: '/farmer/dashboard',          element: <FarmerDashboard />,        role: 'farmer', permission: 'farmer.dashboard' },

  // ═══════════════════════════════════════════════════════════
  // OPERATIONS
  // ═══════════════════════════════════════════════════════════
  { path: '/farmer/farms',              element: <FarmManagement />,         role: 'farmer', permission: 'farmer.farms.view' },
  { path: '/farmer/ponds',              element: <PondsOverview />,          role: 'farmer', permission: 'farmer.ponds.view' },
  { path: '/farmer/ponds/:pondId',      element: <SinglePondDashboard />,    role: 'farmer', permission: 'farmer.ponds.view' },
  { path: '/farmer/stocking-details',   element: <StockingDetails />,        role: 'farmer', permission: 'farmer.ponds.view' },

  // ═══════════════════════════════════════════════════════════
  // FINANCE — new merged pages
  // ═══════════════════════════════════════════════════════════
  { path: '/farmer/credit-escrow',      element: <CreditEscrowPage />,       role: 'farmer', permission: 'farmer.dashboard' },
  { path: '/farmer/harvest-settlement', element: <HarvestSettlementPage />,   role: 'farmer', permission: 'farmer.dashboard' },
  { path: '/farmer/insurance',          element: <InsuranceClaimsPage />,     role: 'farmer', permission: ['farmer.policies.view', 'farmer.claims.view'] },

  // ═══════════════════════════════════════════════════════════
  // TOOLS & SUPPORT
  // ═══════════════════════════════════════════════════════════
  { path: '/farmer/documents',          element: <DocumentsManagement />,    role: 'farmer', permission: 'farmer.dashboard' },
  { path: '/farmer/premium-calculator', element: <PremiumCalculator />,      role: 'farmer', permission: 'farmer.policies.view' },
  { path: '/farmer/calculator',         element: <ClaimCalculator />,        role: 'farmer', permission: 'farmer.claims.view' },
  { path: '/farmer/perils',             element: <PerilsInformation />,      role: 'farmer', permission: 'farmer.dashboard' },
  { path: '/farmer/policy-terms',       element: <PolicyTerms />,            role: 'farmer', permission: 'farmer.policies.view' },
  { path: '/farmer/help',               element: <HelpCenter />,             role: 'farmer', permission: 'farmer.dashboard' },

  // ═══════════════════════════════════════════════════════════
  // KEPT ROUTES (accessible via direct URL, not in sidebar)
  // ═══════════════════════════════════════════════════════════
  { path: '/farmer/kyc',                element: <KYCSubmission />,          role: 'farmer', permission: ['farmer.kyc.submit', 'farmer.kyc.view'] },
  { path: '/farmer/alerts',             element: <AlertsPage />,             role: 'farmer', permission: 'farmer.dashboard' },
  { path: '/farmer/requests',           element: <RequestManagement />,      role: 'farmer', permission: 'farmer.dashboard' },
  { path: '/farmer/policies/:policyId', element: <PolicyDetailView />,       role: 'farmer', permission: 'farmer.policies.view' },

  // ═══════════════════════════════════════════════════════════
  // REDIRECTS — old routes point to new merged pages
  // ═══════════════════════════════════════════════════════════
  { path: '/farmer/policies',           element: <Navigate to="/farmer/insurance" replace />,  role: 'farmer', permission: 'farmer.policies.view' },
  { path: '/farmer/claims',             element: <Navigate to="/farmer/insurance" replace />,  role: 'farmer', permission: ['farmer.claims.view', 'farmer.claims.submit'] },
  { path: '/farmer/insurance-claims',   element: <Navigate to="/farmer/insurance" replace />,  role: 'farmer', permission: ['farmer.policies.view', 'farmer.claims.view'] },
];

export default farmerRoutes;
